import { allNonNullCells, shuffle } from './gridHelpers';
import { HeroId } from './heroes';
import { Cell, COLS, Grid, ROWS, TileKind } from './types';

export type TeamUpResult = {
  clearedCells: Cell[];
  convertedCells: { cell: Cell; newKind: TileKind }[];
  freezeEnemyMs?: number;
  stunAttacks?: number;
  spawnBombs?: Cell[];
  firstAttackBlocked?: boolean;
  extraShake?: boolean;
};

export type TeamUpContext = {
  rng: () => number;
};

export type TeamUpSpec = {
  id: string;
  pairKey: string;
  displayName: string;
  accentColor: number;
  flash: [number, number, number];
  bonusDamage: number;
  execute(grid: Grid, ctx: TeamUpContext): TeamUpResult;
};

export function pairKey(a: HeroId, b: HeroId): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

const HERO_TILE_KINDS: TileKind[] = [
  'shield',
  'claws',
  'repulsor',
  'web',
  'hammer',
  'fist',
  'eye',
  'hex',
  'photon',
  'gem',
  'atom',
  'ghost-web',
  'bolt',
];

function clearRows(count: number, rng: () => number): Cell[] {
  const rows = shuffle(
    Array.from({ length: ROWS }, (_, i) => i),
    rng,
  ).slice(0, count);
  const cells: Cell[] = [];
  for (const r of rows) {
    for (let c = 0; c < COLS; c++) cells.push({ row: r, col: c });
  }
  return cells;
}

function clearCols(count: number, rng: () => number): Cell[] {
  const cols = shuffle(
    Array.from({ length: COLS }, (_, i) => i),
    rng,
  ).slice(0, count);
  const cells: Cell[] = [];
  for (const c of cols) {
    for (let r = 0; r < ROWS; r++) cells.push({ row: r, col: c });
  }
  return cells;
}

function centerRow(): Cell[] {
  const r = Math.floor(ROWS / 2);
  const cells: Cell[] = [];
  for (let c = 0; c < COLS; c++) cells.push({ row: r, col: c });
  return cells;
}

function center3x3(): Cell[] {
  const r = Math.floor(ROWS / 2);
  const c = Math.floor(COLS / 2);
  const cells: Cell[] = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      const nr = r + dr;
      const nc = c + dc;
      if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) continue;
      cells.push({ row: nr, col: nc });
    }
  }
  return cells;
}

function random3x3(grid: Grid, rng: () => number): Cell[] {
  const cells = allNonNullCells(grid);
  if (cells.length === 0) return [];
  const center = shuffle(cells, rng)[0];
  const out: Cell[] = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      const nr = center.row + dr;
      const nc = center.col + dc;
      if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) continue;
      out.push({ row: nr, col: nc });
    }
  }
  return out;
}

function randomClears(grid: Grid, n: number, rng: () => number): Cell[] {
  return shuffle(allNonNullCells(grid), rng).slice(0, n);
}

function convertToKind(
  grid: Grid,
  kind: TileKind,
  n: number,
  rng: () => number,
  excludeKind?: TileKind,
): { cell: Cell; newKind: TileKind }[] {
  const pool = allNonNullCells(grid).filter((c) => {
    const t = grid[c.row][c.col];
    return t && t.kind !== (excludeKind ?? kind);
  });
  return shuffle(pool, rng)
    .slice(0, n)
    .map((cell) => ({ cell, newKind: kind }));
}

function convertToRandomHeroKinds(
  grid: Grid,
  n: number,
  rng: () => number,
): { cell: Cell; newKind: TileKind }[] {
  const picks = shuffle(allNonNullCells(grid), rng).slice(0, n);
  return picks.map((cell) => ({
    cell,
    newKind: HERO_TILE_KINDS[Math.floor(rng() * HERO_TILE_KINDS.length)],
  }));
}

function convertCenterRowToKind(kind: TileKind): { cell: Cell; newKind: TileKind }[] {
  const r = Math.floor(ROWS / 2);
  const out: { cell: Cell; newKind: TileKind }[] = [];
  for (let c = 0; c < COLS; c++) out.push({ cell: { row: r, col: c }, newKind: kind });
  return out;
}

export const GENERIC_FALLBACK: TeamUpSpec = {
  id: 'generic-power-surge',
  pairKey: 'generic',
  displayName: 'Power Surge',
  accentColor: 0xa855f7,
  flash: [168, 85, 247],
  bonusDamage: 6,
  execute: (_grid, ctx) => {
    const rows = shuffle(
      Array.from({ length: ROWS }, (_, i) => i),
      ctx.rng,
    ).slice(0, 1);
    const cols = shuffle(
      Array.from({ length: COLS }, (_, i) => i),
      ctx.rng,
    ).slice(0, 1);
    const cells: Cell[] = [];
    for (const r of rows) for (let c = 0; c < COLS; c++) cells.push({ row: r, col: c });
    for (const c of cols) for (let r = 0; r < ROWS; r++) cells.push({ row: r, col: c });
    return { clearedCells: cells, convertedCells: [] };
  },
};

const NAMED_TEAMUPS: TeamUpSpec[] = [
  {
    id: 'bulwark-beam',
    pairKey: pairKey('captain-america', 'iron-man'),
    displayName: 'Bulwark Beam',
    accentColor: 0xef4444,
    flash: [239, 68, 68],
    bonusDamage: 10,
    execute: () => ({ clearedCells: centerRow(), convertedCells: [] }),
  },
  {
    id: 'worthy',
    pairKey: pairKey('captain-america', 'thor'),
    displayName: 'Worthy',
    accentColor: 0x60a5fa,
    flash: [96, 165, 250],
    bonusDamage: 5,
    execute: (_grid, ctx) => ({ clearedCells: clearCols(2, ctx.rng), convertedCells: [] }),
  },
  {
    id: 'shield-claw',
    pairKey: pairKey('captain-america', 'black-panther'),
    displayName: 'Shield & Claw',
    accentColor: 0xa3a3a3,
    flash: [163, 163, 163],
    bonusDamage: 8,
    execute: (_grid, ctx) => ({ clearedCells: clearRows(3, ctx.rng), convertedCells: [] }),
  },
  {
    id: 'patriot-slam',
    pairKey: pairKey('captain-america', 'hulk'),
    displayName: 'Patriot Slam',
    accentColor: 0x22c55e,
    flash: [34, 197, 94],
    bonusDamage: 12,
    execute: (grid, ctx) => ({
      clearedCells: randomClears(grid, 4, ctx.rng),
      convertedCells: [],
      extraShake: true,
    }),
  },
  {
    id: 'stark-intern',
    pairKey: pairKey('iron-man', 'spider-man'),
    displayName: 'Stark Intern',
    accentColor: 0xfbbf24,
    flash: [251, 191, 36],
    bonusDamage: 8,
    execute: (grid, ctx) => ({
      clearedCells: [],
      convertedCells: convertToKind(grid, 'web', 4, ctx.rng),
      freezeEnemyMs: 2000,
    }),
  },
  {
    id: 'hulkbuster',
    pairKey: pairKey('iron-man', 'hulk'),
    displayName: 'Hulkbuster',
    accentColor: 0xfbbf24,
    flash: [251, 191, 36],
    bonusDamage: 20,
    execute: () => ({ clearedCells: [], convertedCells: [], stunAttacks: 1 }),
  },
  {
    id: 'sky-armor',
    pairKey: pairKey('iron-man', 'thor'),
    displayName: 'Sky Armor',
    accentColor: 0xfde047,
    flash: [253, 224, 71],
    bonusDamage: 6,
    execute: (_grid, ctx) => ({
      clearedCells: [...clearRows(1, ctx.rng), ...clearCols(1, ctx.rng)],
      convertedCells: [],
    }),
  },
  {
    id: 'worldbreaker',
    pairKey: pairKey('thor', 'hulk'),
    displayName: 'Worldbreaker',
    accentColor: 0x22c55e,
    flash: [34, 197, 94],
    bonusDamage: 15,
    execute: () => ({
      clearedCells: center3x3(),
      convertedCells: [],
      extraShake: true,
    }),
  },
  {
    id: 'cosmic-storm',
    pairKey: pairKey('thor', 'captain-marvel'),
    displayName: 'Cosmic Storm',
    accentColor: 0xfbbf24,
    flash: [251, 191, 36],
    bonusDamage: 10,
    execute: () => ({ clearedCells: [], convertedCells: [], stunAttacks: 2 }),
  },
  {
    id: 'smash-swing',
    pairKey: pairKey('hulk', 'spider-man'),
    displayName: 'Smash Swing',
    accentColor: 0xef4444,
    flash: [239, 68, 68],
    bonusDamage: 15,
    execute: (grid, ctx) => ({
      clearedCells: [],
      convertedCells: [],
      spawnBombs: randomClears(grid, 1, ctx.rng),
    }),
  },
  {
    id: 'spider-verse',
    pairKey: pairKey('spider-man', 'ghost-spider'),
    displayName: 'Spider-Verse',
    accentColor: 0xec4899,
    flash: [236, 72, 153],
    bonusDamage: 6,
    execute: (grid, ctx) => ({
      clearedCells: [],
      convertedCells: convertToKind(grid, 'web', 6, ctx.rng),
    }),
  },
  {
    id: 'amazing-friends',
    pairKey: pairKey('spider-man', 'spin'),
    displayName: 'Amazing Friends',
    accentColor: 0xef4444,
    flash: [239, 68, 68],
    bonusDamage: 5,
    execute: (grid, ctx) => ({
      clearedCells: [],
      convertedCells: convertToKind(grid, 'web', 5, ctx.rng),
    }),
  },
  {
    id: 'miles-and-gwen',
    pairKey: pairKey('ghost-spider', 'spin'),
    displayName: 'Miles & Gwen',
    accentColor: 0xec4899,
    flash: [236, 72, 153],
    bonusDamage: 10,
    execute: () => ({ clearedCells: [], convertedCells: [], stunAttacks: 2 }),
  },
  {
    id: 'multiverse-split',
    pairKey: pairKey('doctor-strange', 'scarlet-witch'),
    displayName: 'Multiverse Split',
    accentColor: 0xa855f7,
    flash: [168, 85, 247],
    bonusDamage: 4,
    execute: (grid, ctx) => ({
      clearedCells: [],
      convertedCells: convertToRandomHeroKinds(grid, 9, ctx.rng),
      freezeEnemyMs: 6000,
    }),
  },
  {
    id: 'mind-magic',
    pairKey: pairKey('doctor-strange', 'vision'),
    displayName: 'Mind Magic',
    accentColor: 0xfacc15,
    flash: [250, 204, 21],
    bonusDamage: 4,
    execute: (grid, ctx) => ({
      clearedCells: random3x3(grid, ctx.rng),
      convertedCells: [],
      freezeEnemyMs: 4000,
    }),
  },
  {
    id: 'westview-bond',
    pairKey: pairKey('scarlet-witch', 'vision'),
    displayName: 'Westview Bond',
    accentColor: 0xf43f5e,
    flash: [244, 63, 94],
    bonusDamage: 8,
    execute: () => ({
      clearedCells: [],
      convertedCells: convertCenterRowToKind('hex'),
    }),
  },
  {
    id: 'binary-chaos',
    pairKey: pairKey('scarlet-witch', 'captain-marvel'),
    displayName: 'Binary Chaos',
    accentColor: 0xfbbf24,
    flash: [251, 191, 36],
    bonusDamage: 12,
    execute: (grid, ctx) => ({
      clearedCells: [],
      convertedCells: convertToRandomHeroKinds(grid, 5, ctx.rng),
    }),
  },
  {
    id: 'photon-fusion',
    pairKey: pairKey('vision', 'captain-marvel'),
    displayName: 'Photon Fusion',
    accentColor: 0xfde047,
    flash: [253, 224, 71],
    bonusDamage: 15,
    execute: (_grid, ctx) => ({
      clearedCells: clearRows(2, ctx.rng),
      convertedCells: [],
    }),
  },
  {
    id: 'tech-titans',
    pairKey: pairKey('black-panther', 'iron-man'),
    displayName: 'Tech Titans',
    accentColor: 0xfbbf24,
    flash: [251, 191, 36],
    bonusDamage: 10,
    execute: () => ({
      clearedCells: [],
      convertedCells: [],
      firstAttackBlocked: true,
    }),
  },
  {
    id: 'giant-vs-goliath',
    pairKey: pairKey('ant-man', 'hulk'),
    displayName: 'Giant vs. Goliath',
    accentColor: 0xef4444,
    flash: [239, 68, 68],
    bonusDamage: 18,
    execute: (grid, ctx) => ({
      clearedCells: [],
      convertedCells: [],
      spawnBombs: randomClears(grid, 2, ctx.rng),
    }),
  },
  {
    id: 'weapon-x-protocol',
    pairKey: pairKey('captain-america', 'wolverine'),
    displayName: 'Weapon X Protocol',
    accentColor: 0xfbbf24,
    flash: [251, 191, 36],
    bonusDamage: 10,
    execute: (_grid, ctx) => ({
      clearedCells: clearCols(1, ctx.rng),
      convertedCells: [],
    }),
  },
  {
    id: 'fastball-special',
    pairKey: pairKey('hulk', 'wolverine'),
    displayName: 'Fastball Special',
    accentColor: 0xfbbf24,
    flash: [251, 191, 36],
    bonusDamage: 22,
    execute: () => ({ clearedCells: [], convertedCells: [], extraShake: true }),
  },
  {
    id: 'claws-of-fury',
    pairKey: pairKey('black-panther', 'wolverine'),
    displayName: 'Claws of Fury',
    accentColor: 0xa855f7,
    flash: [168, 85, 247],
    bonusDamage: 12,
    execute: (_grid, ctx) => ({ clearedCells: clearRows(3, ctx.rng), convertedCells: [] }),
  },
  {
    id: 'team-up-training',
    pairKey: pairKey('spider-man', 'wolverine'),
    displayName: 'Team-Up Training',
    accentColor: 0xef4444,
    flash: [239, 68, 68],
    bonusDamage: 8,
    execute: (grid, ctx) => ({
      clearedCells: [],
      convertedCells: convertToKind(grid, 'web', 5, ctx.rng),
    }),
  },
  {
    id: 'tech-and-terror',
    pairKey: pairKey('iron-man', 'rocket'),
    displayName: 'Tech & Terror',
    accentColor: 0xfb923c,
    flash: [251, 146, 60],
    bonusDamage: 10,
    execute: (_grid, ctx) => ({
      clearedCells: clearRows(2, ctx.rng),
      convertedCells: [],
    }),
  },
  {
    id: 'sweet-rabbit',
    pairKey: pairKey('thor', 'rocket'),
    displayName: 'Sweet Rabbit',
    accentColor: 0xfbbf24,
    flash: [251, 191, 36],
    bonusDamage: 12,
    execute: (grid, ctx) => ({
      clearedCells: randomClears(grid, 5, ctx.rng),
      convertedCells: [],
    }),
  },
  {
    id: 'small-packages',
    pairKey: pairKey('ant-man', 'rocket'),
    displayName: 'Small Packages',
    accentColor: 0xef4444,
    flash: [239, 68, 68],
    bonusDamage: 8,
    execute: (grid, ctx) => ({
      clearedCells: [],
      convertedCells: [],
      spawnBombs: randomClears(grid, 2, ctx.rng),
    }),
  },
  {
    id: 'morphin-pair',
    pairKey: pairKey('red-ranger', 'green-ranger'),
    displayName: 'Morphin Pair',
    accentColor: 0x22c55e,
    flash: [34, 197, 94],
    bonusDamage: 14,
    execute: (_grid, ctx) => ({
      clearedCells: clearRows(2, ctx.rng),
      convertedCells: [],
      stunAttacks: 1,
    }),
  },
  {
    id: 'bow-and-sword',
    pairKey: pairKey('red-ranger', 'pink-ranger'),
    displayName: 'Bow & Sword',
    accentColor: 0xf472b6,
    flash: [244, 114, 182],
    bonusDamage: 10,
    execute: (grid, ctx) => ({
      clearedCells: randomClears(grid, 5, ctx.rng),
      convertedCells: [],
      freezeEnemyMs: 2500,
    }),
  },
  {
    id: 'dragon-arrow',
    pairKey: pairKey('green-ranger', 'pink-ranger'),
    displayName: 'Dragon Arrow',
    accentColor: 0xec4899,
    flash: [236, 72, 153],
    bonusDamage: 12,
    execute: () => ({ clearedCells: [], convertedCells: [], freezeEnemyMs: 5000 }),
  },
];

const TEAMUPS_BY_PAIR: Map<string, TeamUpSpec> = new Map(
  NAMED_TEAMUPS.map((t) => [t.pairKey, t]),
);

export const ALL_NAMED_TEAMUPS: TeamUpSpec[] = NAMED_TEAMUPS;

export function resolveTeamUp(a: HeroId, b: HeroId): TeamUpSpec {
  return TEAMUPS_BY_PAIR.get(pairKey(a, b)) ?? GENERIC_FALLBACK;
}
